import Stack from 'components/elements/Stack'
import { Know } from 'interfaces/Know'
import _useTranslation from 'lib/hooks/_useTranslation'
import styled from 'styled-components'
import { Body2, StandardHeadline, StandardSubtitle } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import TaxonomyService from 'services/TaxonomyService'
import { GetServerSideProps } from 'next'
import { parseLocale } from 'lib/constants/localization'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { parseIdFromSlug } from 'lib/formatting/url'
import SubjectService from 'services/SubjectService'
import Meta from 'components/modules/SEO/Meta'
import { Subject } from 'interfaces/Subject'
import FeedKnowCard from 'stories/elements/Know/FeedKnowCard'

const Container = styled(Stack)`
    min-height: 100vh;
    padding: 50px 15px;
    &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
        z-index: -1;
        pointer-events: none;
    }
    .container {
        grid-template-columns: 1fr 1fr;
        @media (max-width: 1050px) {
            grid-template-columns: 1fr;
        }
    }
`

interface Props {
    subject: Subject
    subjectKnows: Know[]
}

const SubjectKnows = ({ subject, subjectKnows }: Props) => {
    const { t } = _useTranslation('subjects')

    return (
        <Container gutter={25}>
            <section>
                <Meta
                    title={t('subjects/bestStudyNotesForSnippet', { knowsCount: subjectKnows.length, topic: subject.name })}
                    description={t('subjects/description')}
                />
                <Stack gutter={25}>
                    <Stack gutter={5}>
                        <StandardHeadline fontFamily="var(--font-poppins)" color={theme.colors.white}>
                            {subject.name}
                        </StandardHeadline>
                        <StandardSubtitle
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                            color={theme.colors.grayScaleSecondary}
                        >
                            {t('subjects/bestStudyNotesForSnippet', {
                                knowsCount: subjectKnows.length,
                                topic: subject.name,
                            })}
                        </StandardSubtitle>
                    </Stack>

                    <Stack gutter={20} className="container">
                        {subjectKnows ? (
                            subjectKnows.map((know) => (
                                <FeedKnowCard
                                    know={know}
                                    key={know.uuid}
                                    referrerScreen="subject"
                                    knowUuid={know.uuid}
                                    isDarkTheme
                                />
                            ))
                        ) : (
                            <Body2 fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.grayScaleSecondary}>
                                {t('subjects/subjectsKnowsSubjectNoKnowsFound')}
                            </Body2>
                        )}
                    </Stack>
                </Stack>
            </section>
        </Container>
    )
}

export default SubjectKnows

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    if (!context.params || typeof context.params.slug !== 'string') {
        return { notFound: true }
    }

    const translations = await serverSideTranslations(parseLocale(context.locale).interfaceLanguageCode, [
        'common',
        'subjects',
    ])

    const subjectId = parseIdFromSlug(context.params.slug)

    if (!subjectId) {
        return { notFound: true }
    }

    const subject = await SubjectService.get(subjectId)

    const subjectKnows = await TaxonomyService.getSubjectKnows(+subjectId)

    return {
        props: {
            subject,
            subjectKnows,
            ...translations,
        },
    }
}
